import React from 'react';
import { motion } from 'framer-motion';
import { CreditCardIcon, DownloadIcon, ShieldCheckIcon } from 'lucide-react';
import { CTAButton, PRICE_SHORT, PRODUCT_SPEC } from './Shared';
import { Bellflower, Blossom, Daisy, DropperBottle, FlowerSpike, Sprig } from './FloralDecor';
const assurances = [{
  icon: CreditCardIcon,
  text: 'One-time purchase'
}, {
  icon: DownloadIcon,
  text: 'Digital PDF access after payment confirmation'
}, {
  icon: ShieldCheckIcon,
  text: '7-day refund policy'
}];
const productImage = 'https://cdn.magicpatterns.com/uploads/1qeqsXoTiEaSY6X5bbqr6L/1.1.png';
export function Hero() {
  return <header className="rice-paper relative overflow-hidden border-b border-[#e3d9c6] pb-10 pt-5 text-stone-900 md:pb-16 md:pt-8 lg:pt-10" data-id="element-119" data-mp="sa02lrfuzorh">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden" data-id="element-120" data-mp="3d1ildbzsjgoh">
        <Sprig className="absolute -left-6 top-6 h-72 w-44 text-emerald-700/15 md:h-96 md:w-56" data-id="element-121" data-mp="3cz5wsfzj282g" />
        <Sprig className="absolute -right-8 top-24 h-72 w-44 -scale-x-100 text-emerald-700/10 md:h-[26rem] md:w-56" data-id="element-122" data-mp="384s5aonzkl32" />
        <Blossom className="absolute left-[3%] top-[58%] h-14 w-14 md:h-20 md:w-20" petalClassName="text-pink-300/70" centerClassName="text-amber-400" data-id="element-123" data-mp="3o7c05xxxspgm" />
        <Daisy className="absolute right-[4%] top-[6%] h-12 w-12 md:h-16 md:w-16" petalClassName="text-amber-100" centerClassName="text-amber-400" data-id="element-124" data-mp="3e9fuvmrokoqf" />
        <FlowerSpike className="absolute -bottom-4 left-[42%] hidden h-40 w-12 lg:block" petalClassName="text-violet-300/60" centerClassName="text-emerald-600/40" data-id="element-125" data-mp="30g6l3kztm17d" />
        <Bellflower className="absolute bottom-0 right-[2%] hidden h-44 w-24 md:block" petalClassName="text-violet-300/60" centerClassName="text-emerald-600/40" data-id="element-126" data-mp="2viy1rh2xrr4j" />
        <Daisy className="absolute bottom-8 left-[18%] hidden h-10 w-10 lg:block" petalClassName="text-rose-200" centerClassName="text-orange-300" data-id="element-127" data-mp="2lkqyu25hf9lj" />
        <DropperBottle className="absolute bottom-6 left-[5%] hidden h-32 w-16 text-amber-700/15 lg:block" data-id="element-128" data-mp="s3uq3ug3l49k" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8 lg:text-left" data-id="element-129" data-mp="ibyqgeyd5638">
        <motion.div initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} className="grid items-center lg:grid-cols-[1.05fr_1fr] lg:gap-x-12" data-id="element-130" data-mp="ddf3cb819o9a">
          <div className="lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-131" data-mp="162m0p6vr47sy">
            <p className="mb-3 text-xs font-semibold tracking-[.2em] text-amber-800 md:text-sm" data-id="element-132" data-mp="111okv0y7rpum">LUMEN AKASHA™</p>
            <h1 className="text-[30px] font-bold leading-[1.08] tracking-tight text-emerald-900 md:text-5xl lg:text-[clamp(2.6rem,4.4vw,3.75rem)] lg:leading-[1.05] lg:tracking-[-0.03em]" data-id="element-133" data-mp="x0crqlg4smho">
              Bring more structure to your <span className="text-rose-700" data-id="element-134" data-mp="3hq25sw90g49b">Bach Flower consultations.</span>
            </h1>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-stone-700 md:mt-4 md:text-lg lg:mx-0" data-id="element-135" data-mp="1xb5qyhfqoxhp">
              A practical, client-led guide to organizing consultation conversations, exploring traditional formula
              studies and case examples, and keeping useful session tools in one place.
            </p>
          </div>

          <div className="mt-5 flex justify-center md:mt-6 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:mt-0" data-id="element-136" data-mp="2g0c13g2gwgrw">
            <motion.img src={productImage} alt="Bach Flower Consultation Guide digital edition mockup: consultation guide with session worksheets, dropper bottles, flowers, and a seal reading 3 Volumes, 5 Bonuses" initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.3,
            ease: [0.23, 1, 0.32, 1]
          }} className="h-auto w-[92%] max-w-[520px] drop-shadow-2xl" draggable={false} fetchPriority="high" data-id="element-137" data-mp="2kxublaw4v420" />
          </div>

          <div className="mt-5 flex flex-col md:mt-6 lg:col-start-1 lg:row-start-2 lg:mt-7 lg:self-start" data-id="element-138" data-mp="3vi6z6n0q6euq">
            <div className="order-2 mt-4 lg:order-1 lg:mt-0" data-id="element-139" data-mp="e9tpvocs9t0r">
              <h2 className="text-sm font-bold tracking-wide text-emerald-800 md:text-lg" data-id="element-140" data-mp="e77j3qxpvhjl">
                BACH FLOWER CONSULTATION GUIDE — COMPLETE DIGITAL EDITION
              </h2>
              <p className="mt-1 text-xs font-medium text-stone-600 md:text-base" data-id="element-141" data-mp="3ho688fg0lw4x">{PRODUCT_SPEC}</p>
            </div>
            <div className="order-1 lg:order-2 lg:mt-5" data-id="element-142" data-mp="3rg3k7oeuks4p">
              <CTAButton className="w-full px-7 py-4 text-sm sm:w-auto md:px-10 md:py-5 md:text-xl" data-id="element-143" data-mp="3rgve8ussqhsv">
                GET THE COMPLETE GUIDE — {PRICE_SHORT}
              </CTAButton>
            </div>
            <ul className="order-3 mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-stone-700 md:text-sm lg:justify-start" data-id="element-144" data-mp="2yyhpdkpfu65x">
              {assurances.map(({
              icon: Icon,
              text
            }) => <li key={text} className="flex items-center gap-1.5" data-id="element-145" data-mp="19hcfm3zqhn9k">
                  <Icon className="h-4 w-4 shrink-0 text-rose-600" aria-hidden="true" data-id="element-146" data-mp="1eafb4ho2n6se" />
                  <span data-id="element-147" data-mp="zl95aauqaibj">{text}</span>
                </li>)}
            </ul>
            <p className="order-4 mx-auto mt-5 max-w-xl border-t border-[#e3d9c6] pt-4 text-xs leading-relaxed text-stone-500 lg:mx-0" data-id="element-148" data-mp="14j18i10tpqv1">
              Digital product only. Spiral-bound books shown in product images are mockups; no physical item is shipped.
            </p>
          </div>
        </motion.div>
      </div>
    </header>;
}